import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import * as z from 'zod';
import formMessages from '~/components/form-messages';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useRoutes } from '~/hooks/api/useRoutes';
import messages from './messages';
import { useAppForm } from '../Form';

const createFormSchema = (intl: IntlShape) =>
  z.object({
    reason: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, { message: intl.formatMessage(formMessages.required) })
      .max(30, {
        message: intl.formatMessage(messages.maxChars, {
          maxNumberOfChars: 30,
        }),
      }),
    routeIds: z.array(z.string()),
  });

type EmergencyModeFormProps = {
  onSubmit: (values: { reason: string; routeIds: number[] }) => void;
};

const EmergencyModeForm = (props: EmergencyModeFormProps) => {
  const intl = useIntl();
  const form = useAppForm({
    defaultValues: {
      reason: '',
      routeIds: [] as string[],
    },
    validators: {
      onChange: createFormSchema(intl),
    },
    onSubmit: ({ value }) => {
      props.onSubmit({
        reason: value.reason,
        routeIds: value.routeIds.map((routeId) => parseInt(routeId, 10)),
      });
    },
  });
  const { data } = useRoutes();
  const activeRoutes = (data ?? []).filter((route) => !route.isSuspended);
  return (
    <Box sx={{ p: 2 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Stack spacing={2}>
          <form.AppField name="reason">
            {(field) => (
              <field.TextField
                label={<FormattedMessage {...messages.reason} />}
                multiline
              />
            )}
          </form.AppField>
          <form.AppField name="routeIds">
            {(field) => (
              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend">
                  <FormattedMessage {...messages.route} />
                </FormLabel>
                <FormGroup>
                  {activeRoutes.map((route) => {
                    const value = route.id.toString();
                    const checked = field.state.value.includes(value);
                    return (
                      <FormControlLabel
                        key={route.id}
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={(e) => {
                              const nextValue = e.target.checked
                                ? [...field.state.value, value]
                                : field.state.value.filter(
                                    (routeId) => routeId !== value
                                  );
                              field.handleChange(nextValue);
                            }}
                          />
                        }
                        label={route.name}
                      />
                    );
                  })}
                </FormGroup>
              </FormControl>
            )}
          </form.AppField>
          <div>
            <Typography variant="body2">
              <FormattedMessage {...messages.emergencyModeDescription} />
            </Typography>
          </div>
          <div>
            <form.AppForm>
              <form.SubmitButton>
                <FormattedMessage {...messages.save} />
              </form.SubmitButton>
            </form.AppForm>
          </div>
        </Stack>
      </form>
    </Box>
  );
};

export default EmergencyModeForm;
